/* istanbul ignore file: currently content service still returns fake data for some content types. Please remove this comment after all content types are using real data. */
import { ContentType } from '../models/ContentType';
import { SearchResultList, SearchResultItem, MixedContentTypeSearchResult } from '../models/SearchResultTypes';
import { FilterQuery } from '../state/FilterState';
import { CRASSService } from './CRASSService';
import { ContentDefinitions } from '../models/contentDefinitions';
import { WebClientResourceService } from './httpClient/WebClientResourceService';
import { IPagedItemsParams } from '../models/httpClient/IListFetcher';
import { SortSelection } from '../models/SortSelection';
import { IUserWithPublicInfo } from '../models/httpClient/MetaDataResults';
import GetMetaDataRequests from './httpClient/GetMetaDataRequests';
import { FavoriteService } from './FavoriteService';
import { SearchResponseHitsContent } from '../../generated/content-exploration';

export const CRASSResponseMaxCount = 10000;

export class ContentService {
  public static instance = new ContentService();

  public fetchAllContent(
    searchTerm: string,
    startIndex: number,
    pageSize: number,
    filter?: FilterQuery
  ): Promise<MixedContentTypeSearchResult> {
    return new Promise<MixedContentTypeSearchResult>((resolve, reject) => {
      const mixedContentTypeResult: MixedContentTypeSearchResult = {
        contentTypeOrder: [],
        map: {} as { [type in ContentType]: SearchResultList<SearchResultItem> },
      };
      const promises: Promise<SearchResultList<SearchResultItem>>[] = [];
      for (const contentType in ContentDefinitions) {
        const currentContentType = contentType as ContentType;
        promises.push(ContentService.instance.fetchContent(currentContentType, searchTerm, startIndex, pageSize, filter));
      }

      Promise.all(promises).then((results) => {
        const resultLists: SearchResultList<SearchResultItem>[] = results;

        const list: { type: ContentType; score: number }[] = [];
        let errorCount = 0;
        const callCount = resultLists.length;

        mixedContentTypeResult.map = resultLists.reduce<{ [type in ContentType]: SearchResultList<SearchResultItem> }>((p, c) => {
          p[c.type] = c;
          errorCount += c.error ? 1 : 0;

          const score =
            c.list.reduce<number>((p, c) => {
              return p + (c.crassData?.score || 0);
            }, 0) / c.list.length;
          list.push({ type: c.type, score: score || 0 });

          return p;
        }, {} as { [type in ContentType]: SearchResultList<SearchResultItem> });
        if (callCount === errorCount) {
          mixedContentTypeResult.error = Object.values(mixedContentTypeResult.map)[0].error;
        }
        const sortedList = list.sort((a, b) => b.score - a.score);
        const contentTypeOrder = sortedList.map((item) => item.type);
        mixedContentTypeResult.contentTypeOrder = contentTypeOrder;

        resolve(mixedContentTypeResult);
      });
    });
  }

  public fetchContent(
    type: ContentType,
    searchTerm: string,
    startIndex: number,
    pageSize: number,
    filter?: FilterQuery,
    sortBy?: SortSelection
  ): Promise<SearchResultList<SearchResultItem>> {
    return new Promise<SearchResultList<SearchResultItem>>((resolve, reject) => {
      if (ContentDefinitions[type].shouldBeAvailable()) {
        CRASSService.instance.search(type, searchTerm, startIndex, pageSize, filter, sortBy).then((crassResponse) => {
          if (crassResponse.error) {
            resolve({
              type: type,
              list: [],
              totalCount: crassResponse.totalCount,
              error: crassResponse.error,
            });
          } else {
            const result: SearchResultList<SearchResultItem> = {
              type: type,
              list: [],
              totalCount: crassResponse.totalCount,
            };
            const crassResponseList = crassResponse.list;

            ContentService.instance.postProcessSearchResult(result, crassResponseList);

            const mapCRASSData = ContentDefinitions[type].mapCRASSData;
            if (mapCRASSData) {
              result.list = crassResponse.list.length > 0 ? mapCRASSData(crassResponseList) : [];
            }
            resolve(result);
          }
        });
      } else {
        resolve({
          type: type,
          list: [],
          totalCount: 0,
          error: ContentServiceError.SHOULD_NOT_BE_AVAILABLE,
        });
      }
    });
  }

  public fetchContentMetadata(
    type: ContentType,
    searchResultItems: SearchResultItem[]
  ): Promise<{ contentType: ContentType; data: SearchResultItem[] } | null> {
    return new Promise<{ contentType: ContentType; data: SearchResultItem[] } | null>((resolve, reject) => {
      if (searchResultItems.length && ContentDefinitions[type].shouldBeAvailable()) {
        const ids = searchResultItems.map((item) => {
          const customID = ContentDefinitions[type].getCustomID;
          return customID ? customID(item) : item.id.toString();
        });
        const getCustomMetaData = ContentDefinitions[type].getCustomMetaDataRequests;
        if (getCustomMetaData) {
          getCustomMetaData(ids).then((response) => {
            const mapMetadata = ContentDefinitions[type].mapMetadata;
            let result: SearchResultItem[] = [];
            if (mapMetadata) {
              result = mapMetadata(searchResultItems, response);
            }
            resolve({ contentType: type, data: result });
          });
        } else if (ContentDefinitions[type].getMetaDataRequests) {
          new WebClientResourceService(ContentDefinitions[type].getMetaDataRequests)
            .fetchList({
              filter: {
                operator: 'and',
                clauses: [{ operator: 'in', field: 'id', values: ids }],
              },
            } as IPagedItemsParams)
            .then((response) => {
              const mapMetadata = ContentDefinitions[type].mapMetadata;
              let result: SearchResultItem[] = [];
              if (mapMetadata) {
                result = mapMetadata(searchResultItems, response);
              }
              resolve({ contentType: type, data: result });
            })
            .catch((error) => {
              resolve(null);
            });
        }
      } else {
        resolve(null);
      }
    });
  }

  public getMissingUser(userId: string): Promise<{ result: IUserWithPublicInfo } | null> {
    return new Promise<{ result: IUserWithPublicInfo } | null>((resolve) => {
      new WebClientResourceService(GetMetaDataRequests.GetUsersRequest)
        .fetchList({
          filter: {
            operator: 'and',
            clauses: [{ operator: 'in', field: 'id', values: [userId] }],
          },
        } as IPagedItemsParams)
        .then((response) => {
          if (response.total) {
            resolve({ result: response.items.users[0] });
          } else {
            resolve(null);
          }
        })
        .catch(() => {
          resolve(null);
        });
    });
  }

  public addFavorite(item: SearchResultItem): Promise<void> {
    return FavoriteService.instance.addFavorite(item);
  }

  public removeFavorite(item: SearchResultItem): Promise<void> {
    return FavoriteService.instance.removeFavorite(item);
  }

  private postProcessSearchResult(result: SearchResultList<SearchResultItem>, crassResponseList: SearchResponseHitsContent[]) {
    if (result.type === ContentType.unifieddatasource) {
      // The following processing of data source related search results is due to Defect 1502405: Duplicate results in the search bar on top of UI for the same object after the upgrade vs searching in Explore pane.
      // Based on the current server side implementation, the data source search results are actually about data connections. One data source may have multiple data connections.
      // When the user searches data source related keywords, multiple data connections may be returned on rendered on the UI with the parent data source information.
      // It looks like the UI is rendering duplicated data source results.
      // Based on engineering cost and impact considering, it's decided that the client side will dedup the data source search result to avoid duplicated information being rendered on the UI.
      const dataSourceMap: { [id: string]: boolean } = {};
      for (let i = 0; i < crassResponseList.length; i++) {
        const crassResponseListItem = crassResponseList[i];
        const dataSourceId = crassResponseListItem.datasourceId || '';
        if (dataSourceMap[dataSourceId]) {
          crassResponseList.splice(i, 1);
          if (result.totalCount <= CRASSResponseMaxCount) {
            // Only fix the total count when the total count is less than the CRASS response max count (currently is 10000);
            // 10000 in CRASS response total count means 10000 or even more.
            // So better not update the total count when it's 10000 to change "10000+" to "9999" (which is incorrect).
            result.totalCount--;
          }
          i--;
        }
        dataSourceMap[dataSourceId] = true;
      }
    }
  }
}

export enum ContentServiceError {
  SHOULD_NOT_BE_AVAILABLE = 'CONTENT_TYPE/SHOULD_NOT_BE_AVAILABLE',
}
